import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ChevronRight, ClipboardCheck, ShoppingBag } from "lucide-react";

import { Button } from "@/components/ui/button";

const GOLD = "#d4af37";

export default function NumerolojiSuccess() {
  const [searchParams] = useSearchParams();
  const requestId = useMemo(() => (searchParams.get("requestId") ?? "").trim(), [searchParams]);

  return (
    <div className="min-h-dvh bg-[#050508] text-slate-200">
      <div
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.14) 0%, rgba(10,10,12,0) 55%), radial-gradient(circle at 20% 25%, rgba(127,13,242,0.10) 0%, rgba(10,10,12,0) 55%)",
        }}
      />

      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#050508]/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4">
          <Link to="/numeroloji" className="flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center border border-[#d4af37] rotate-45">
              <span className="h-3 w-3 bg-[#d4af37]" />
            </span>
            <span className="font-display text-lg font-bold tracking-[0.16em] text-white">Numeroloji.app</span>
          </Link>

          <div className="flex items-center gap-3 sm:gap-6">
            <Link
              to="/panel"
              className="rounded-full border border-[rgba(212,175,55,0.35)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-100 transition-colors hover:bg-[rgba(212,175,55,0.14)] hover:text-white sm:text-xs"
            >
              Panele Git
            </Link>
            <Link
              to="/numeroloji/magaza"
              className="font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:text-sm"
            >
              MAĞAZA
            </Link>
            <Link to="/numeroloji/sepet" className="relative" aria-label="Sepet">
              <ShoppingBag className="h-5 w-5 text-slate-200" />
            </Link>
          </div>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="mb-10">
          <h1 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Teşekkürler</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            <Link to="/numeroloji" className="transition-colors hover:text-[rgba(212,175,55,0.95)]">
              Ana Sayfa
            </Link>
            <ChevronRight className="h-4 w-4 opacity-60" />
            <span style={{ color: GOLD }}>Başarılı</span>
          </div>
        </div>

        <section
          className="rounded-3xl border p-6 shadow-2xl sm:p-10"
          style={{
            borderColor: "rgba(212,175,55,0.18)",
            background:
              "radial-gradient(circle at 70% 10%, rgba(212,175,55,0.12) 0%, transparent 45%), linear-gradient(145deg, rgba(26,18,37,0.55), rgba(10,10,12,0.72))",
          }}
        >
          <div className="flex flex-col items-center text-center">
            <div
              className="grid h-20 w-20 place-items-center rounded-full border"
              style={{ backgroundColor: "rgba(212,175,55,0.14)", borderColor: "rgba(212,175,55,0.28)" }}
            >
              <CheckCircle2 className="h-10 w-10" style={{ color: GOLD }} />
            </div>

            <div className="mt-6 font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">
              Analiz talebiniz alındı
            </div>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
              Raporunuz hazırlanıp e-posta ile PDF olarak gönderilecektir.
            </p>

            <div
              className="mt-8 w-full max-w-xl rounded-2xl border p-5 text-left"
              style={{ borderColor: "rgba(212,175,55,0.12)", backgroundColor: "rgba(0,0,0,0.20)" }}
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <ClipboardCheck className="h-4 w-4" style={{ color: GOLD }} />
                Durum
              </div>
              <div className="mt-1 text-sm text-slate-400">
                {requestId ? `İstek numarası: ${requestId}` : "İstek numarası alınamadı."}
              </div>
              <div className="mt-4 text-xs leading-relaxed text-slate-500">
                Eğer e-posta gelmezse spam kutusunu kontrol edin. Gerekirse yönetici panelinden manuel gönderim yapılabilir.
              </div>
            </div>

            <div className="mt-8 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
              <Button asChild className="rounded-full">
                <Link to="/numeroloji">Ana sayfaya dön</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-full border-[rgba(212,175,55,0.35)] bg-transparent text-slate-100 hover:bg-[rgba(212,175,55,0.12)] hover:text-white"
              >
                <Link to="/numeroloji/magaza">Yeni satın alma</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <footer className="mt-20 border-t py-12" style={{ borderColor: "rgba(212,175,55,0.10)" }}>
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4">
          <div className="h-24 w-px bg-gradient-to-b from-[rgba(212,175,55,0.95)] to-transparent" />
          <div className="text-center">
            <div className="font-display text-3xl tracking-widest" style={{ color: GOLD }}>
              Numeroloji.app
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.22em] opacity-40">
              Evrensel Sayılar Rehberi © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
